/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  FormController,
  Link,
  Typography,
  Tile,
  TileGroup,
  Alert,
  Skeleton,
  SkeletonLine,
  LoadingButton,
} from '@one-thd/sui-atomic-components';
import { RadioButtonUnchecked, ConfirmedFilled } from '@one-thd/sui-icons';
import { IUserData, usersApi } from '../../../api/b2b/users';
import { sortUsers } from '../../../utils/user';
import { ERR_GENERIC_WITH_CANCEL } from '../../../constants/response-error-messages';
import { CHANGES_SAVED_MSG } from '../../../constants/response-success-messages';
import {
  getUsersForTenderRequest,
  updateUsersForTenderRequest,
} from '../../../api/b2b/payments';
import { createUpdateUsersForTenderRequestPayload } from '../../../utils/update-permissions';
import { IDrawerBasicProps } from '../../../models/common/drawer';

export interface ISelectPhaPermissions extends IDrawerBasicProps {
  onSuccess: (message: string) => void;
  paymentId: string;
}

export const SelectPhaPermissions = ({
  hideDrawer,
  onSuccess,
  paymentId,
}: ISelectPhaPermissions) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const initiallySelectedUsers = useRef<string[]>([]);
  const [allUsers, setAllUsers] = useState<IUserData[]>([]);
  const [isError, setIsError] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const totalUsers = allUsers.length;

  const handleTileChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    newFormats: string[],
  ) => {
    setSelectedUsers(newFormats);
  };

  const handleSelectAll = () => {
    if (allUsers.length === selectedUsers.length) {
      setSelectedUsers([]);
    } else {
      const allUserIds = allUsers.map((user) => user.userId.toString());
      setSelectedUsers(allUserIds);
    }
  };

  const getSelectAllText = () => {
    if (allUsers.length === selectedUsers.length) {
      return `Deselect All (${selectedUsers.length} selected)`;
    }
    if (selectedUsers.length > 0) {
      return `Select All (${selectedUsers.length} selected)`;
    }
    return 'Select All';
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const response = await usersApi.getUsers();
      const users = response.data?.users || [];
      const filteredUsers = users.filter(
        (user: IUserData) => user.role.toUpperCase() !== 'RUNNER',
      );
      setAllUsers(sortUsers(filteredUsers));
    };

    const fetchPreExistingUsers = async () => {
      const { userIds } = await getUsersForTenderRequest(paymentId);
      initiallySelectedUsers.current = [...userIds];
      setSelectedUsers([...userIds]);
    };

    setIsError(false);
    setLoadingUsers(true);
    Promise.all([fetchUsers(), fetchPreExistingUsers()])
      .catch(() => {
        handleError();
      })
      .finally(() => {
        setLoadingUsers(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = () => {
    onSuccess(CHANGES_SAVED_MSG.message);
    hideDrawer();
  };
  const handleError = () => {
    setIsError(true);
  };

  const handleSaveChanges = () => {
    const request = createUpdateUsersForTenderRequestPayload(
      selectedUsers,
      initiallySelectedUsers.current,
    );

    if (
      !request.updateUsersForTenderRequest?.authorizeUsers &&
      !request.updateUsersForTenderRequest?.deauthorizeUsers
    ) {
      hideDrawer();
      return Promise.resolve();
    }

    setIsError(false);
    return updateUsersForTenderRequest(paymentId, request)
      .then(({ success }) => {
        if (!success) {
          handleError();
          return;
        }
        handleSuccess();
      })
      .catch(() => {
        handleError();
      });
  };

  const SkeletonLoader = () => {
    return (
      <div className="sui-ml-[-24px]">
        <Skeleton className="sui-grow" data-testid="skeleton-loader">
          <SkeletonLine numberOfLines={3} />
        </Skeleton>
      </div>
    );
  };

  return (
    <>
      <DrawerHeader
        title={`Users (${totalUsers})`}
        onClose={() => {
          hideDrawer();
        }}
      />
      <DrawerBody>
        <div className="sui-flex sui-flex-col sui-justify-between sui-h-full">
          <div className="sui-mb-4">
            <div className="sui-mb-2">
              <Typography variant="body-base" weight="bold">
                Select which users should have permissions to use this line of
                credit.
              </Typography>
              <Typography>
                You may grant permissions to Admins and Purchasers on your
                account to use this line of credit when checking out on
                HomeDepot.com. The balance of this line of credit will be
                available to all users you grant permissions to.
              </Typography>
            </div>
            <div className="sui-mb-4">
              <Link
                component="button"
                onClick={handleSelectAll}
                data-testid="select-text"
              >
                {getSelectAllText()}
              </Link>
            </div>
            {loadingUsers && <SkeletonLoader />}
            {!loadingUsers && (
              <FormController fullWidth>
                <TileGroup
                  className="sui-grid sui-grid-cols-1 sui-gap-4"
                  value={selectedUsers}
                  onChange={handleTileChange}
                  aria-label="permissions selection"
                  disableExclusive
                  data-testid="permissions-tiles"
                >
                  {allUsers.map((user) => (
                    <Tile
                      key={user.userId.toString()}
                      value={user.userId.toString()}
                      aria-label={`Select ${user.firstName} ${user.lastName}`}
                      grow
                    >
                      <div className="sui-flex sui-flex-col sui-w-full">
                        <div className="sui-flex sui-justify-between sui-items-center sui-w-full">
                          <Typography className="sui-truncate sui-text-left">
                            {user.firstName + ' ' + user.lastName}
                          </Typography>
                          <div className="sui-ml-4 sui-flex sui-justify-end">
                            {selectedUsers.includes(user.userId.toString()) ? (
                              <ConfirmedFilled
                                size="small"
                                color="primary"
                                data-testid="confirmed-checkbox"
                              />
                            ) : (
                              <RadioButtonUnchecked
                                size="small"
                                data-testid="radio-button-unchecked"
                              />
                            )}
                          </div>
                        </div>
                        <div className="sui-w-full">
                          <Typography
                            variant="body-sm"
                            color="subtle"
                            className="sui-text-left"
                          >
                            {user.role}
                          </Typography>
                        </div>
                      </div>
                    </Tile>
                  ))}
                </TileGroup>
              </FormController>
            )}
          </div>
          {isError && (
            <div
              data-testid="select-pha-drawer-alert"
              className="sui-mb-[-18px]"
            >
              <Alert status="error">{ERR_GENERIC_WITH_CANCEL.message}</Alert>
            </div>
          )}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className="sui-flex sui-flex-col sui-gap-4">
          <LoadingButton
            fullWidth
            loading={isSaveLoading}
            variant="primary"
            onClick={() => {
              setIsSaveLoading(true);
              handleSaveChanges().finally(() => {
                setIsSaveLoading(false);
              });
            }}
          >
            Save Changes
          </LoadingButton>
          <Button
            fullWidth
            variant="secondary"
            onClick={() => {
              setIsError(false);
              hideDrawer();
            }}
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};
